import { formatMoney } from '@theme/utilities';
import { CartUpdatedEvent, VariantUpdatedEvent } from '@theme/events';

class BundleOffers extends HTMLElement {
  #defaultErrorText;
  connectedCallback() {
    const dataEl = this.querySelector('[data-ref="bundle-data"]');
    this.data = JSON.parse(dataEl.textContent);

    this.pawsIndex = parseInt(this.dataset.pawsPosition, 10) - 1;
    this.finitionIndex = parseInt(this.dataset.finitionPosition, 10) - 1;
    this.guideVariantId = this.dataset.guideVariantId;
    this.boxVariantId = this.dataset.boxVariantId;
    this.giftBadge = this.dataset.giftBadge;
    this.pouchVariantId = this.dataset.pouchVariantId;
    this.pouchPrice = parseInt(this.dataset.pouchPrice, 10) || 0;
    this.pouchProperty = this.dataset.pouchProperty || 'Prénom pochette';
    this.productUrl = this.dataset.productUrl;
    this.productId = this.dataset.productId;
    this.section = this.closest('.shopify-section');
    this.galleryAbort = null;

    this.state = {
      finition: this.dataset.defaultFinition,
      paws: this.dataset.defaultPaws,
    };

    this.names = [];

    this.finitionChips = this.querySelectorAll('[data-role="finition"]');
    this.pawsChips = this.querySelectorAll('[data-role="paws"]');
    this.namesGrid = this.querySelector('[data-ref="names-grid"]');
    this.atc = this.querySelector('[data-ref="atc"]');
    this.errorEl = this.querySelector('[data-ref="error"]');
    this.pouchToggle = this.querySelector('[data-ref="pouch-toggle"]');
    this.pouchField = this.querySelector('[data-ref="pouch-field"]');
    this.pouchInput = this.querySelector('[data-ref="pouch-input"]');
    this.pouchGrid = this.querySelector('[data-ref="pouch-grid"]');
    this.pouchHint = this.querySelector('[data-ref="pouch-hint"]');
    this.apercuSvg = this.querySelector('[data-ref="apercu-svg"]');
    this.apercuG   = this.querySelector('[data-ref="apercu-pattes"]');
    this.apercuEl  = this.querySelector('[data-ref="apercu"]');
    this.apercuLegende = this.querySelector('[data-ref="apercu-legende"]');

    this.#bindEvents();
    this.#renderNameFields();
    this.#construireApercu();
    if (document.fonts) {
      try { document.fonts.load('16px "Amatic SC"').then(() => this.#majApercu(), () => {}); } catch (e) {}
      if (document.fonts.ready) document.fonts.ready.then(() => this.#majApercu());
    }
    this.#updatePrices();
  }

  #bindEvents() {
    this.finitionChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.state.finition = chip.dataset.value;
        this.#setActiveChip(this.finitionChips, chip);
        this.#majApercu();
        this.#updatePrices();
        this.#updateGalleryImage();
      });
    });

    this.pawsChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.state.paws = chip.dataset.value;
        this.#setActiveChip(this.pawsChips, chip);
        this.#renderNameFields();
        this.#construireApercu();
        this.#renderPouchDateFields();
        this.#updatePrices();
        this.#updateGalleryImage();
      });
    });

    this.atc.addEventListener('click', () => this.#addToCart());

    if (this.pouchToggle) {
      this.pouchToggle.addEventListener('change', () => {
        const on = this.pouchToggle.checked;
        if (this.pouchField) this.pouchField.hidden = !on;
        if (on) this.#renderPouchDateFields();
        this.#updatePrices();
      });
    }
  }

  #pouchSelected() {
    return !!(this.pouchToggle && this.pouchToggle.checked && this.pouchVariantId);
  }

  #renderPouchDateFields() {
    if (!this.pouchGrid) return;
    const count = this.#pawsCount();
    const existing = [...this.pouchGrid.querySelectorAll('input')];
    const saved = existing.map(i => i.value);

    const fields = [];
    for (let i = 0; i < count; i++) {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'bundle-name-input pouch-upsell__input';
      // 10 = longueur du plus long format accepté (JJ/MM/AAAA)
      input.maxLength = 10;
      input.autocomplete = 'off';
      // Clavier texte volontaire : le pave numerique (inputmode=numeric) de l'iPhone n'a ni « / » ni « - »,
      // ce qui rendait 03/24 et 2012-2024 impossibles a saisir. Sur iPhone, la touche « 123 » donne
      // les chiffres ET les separateurs.
      input.setAttribute('inputmode', 'text');
      // Pas de « facultatif » sur le champ : a une seule patte, l'option cochee rend
      // la date obligatoire. L'info « on peut laisser vide » n'a de sens qu'a
      // plusieurs pattes, elle vit donc uniquement dans la ligne d'aide.
      input.placeholder = count > 1
        ? `Date patte n°${i + 1} — ex. 14/03/24`
        : 'ex. 14/03/24';
      input.dataset.index = String(i);
      if (saved[i]) input.value = saved[i];
      input.addEventListener('input', () => {
        // On n'autorise physiquement que les chiffres et les séparateurs :
        // impossible de saisir un prénom ou une phrase. Le point et l'espace
        // sont admis a la saisie, #normaliserDate() les convertit ensuite.
        const cleaned = input.value.replace(/[^0-9/.\- ]/g, '');
        if (cleaned !== input.value) {
          const pos = input.selectionStart - (input.value.length - cleaned.length);
          input.value = cleaned;
          try { input.setSelectionRange(pos, pos); } catch (e) {}
        }
        input.classList.remove('bundle-name-input--error');
        if (this.errorEl) this.errorEl.hidden = true;
      });
      // On remet la date dans sa forme gravable des que la cliente quitte le champ :
      // elle voit tout de suite ce qui sera reellement grave.
      input.addEventListener('blur', () => {
        const propre = this.#normaliserDate(input.value);
        if (propre) input.value = propre;
      });
      fields.push(input);
    }
    this.pouchGrid.replaceChildren(...fields);

    if (this.pouchHint) {
      this.pouchHint.textContent = count > 1
        ? 'Ex. 14/03/24, 2012-2024, 03/24 ou 2024. Laissez vide les pattes que vous ne souhaitez pas dater.'
        : 'Ex. 14/03/24, 2012-2024, 03/24 ou 2024.';
    }
  }

  // Accepte les dates ecrites « comme on les ecrit a la main » (14/3/2024, 14.03.2024,
  // 3/24, annees inversees…) et les ramene a l'un des 4 formats gravables.
  // Renvoie '' si la saisie ne correspond a aucun format.
  #normaliserDate(raw) {
    const brut = (raw || '').trim();
    if (brut === '') return '';

    const parts = brut.split(/[\/.\-\s]+/).filter((x) => x !== '');
    if (parts.some((x) => !/^\d+$/.test(x))) return '';

    let candidat = '';
    if (parts.length === 3) {
      // jour / mois / annee — on grave l'annee sur 2 chiffres (JJ/MM/AA),
      // que la cliente ait tape 2024 ou 24.
      const [j, mo, an] = parts;
      if (j.length > 2 || mo.length > 2) return '';
      if (an.length !== 2 && an.length !== 4) return '';
      candidat = `${j.padStart(2, '0')}/${mo.padStart(2, '0')}/${an.slice(-2)}`;
    } else if (parts.length === 2) {
      const [a, b] = parts;
      if (a.length === 4 && b.length === 4) {
        // periode de vie : on remet les annees dans l'ordre si elles sont inversees
        const x = +a, y = +b;
        candidat = x <= y ? `${a}-${b}` : `${b}-${a}`;
      } else if (a.length <= 2 && (b.length === 2 || b.length === 4)) {
        // mois / annee courte
        candidat = `${a.padStart(2, '0')}/${b.slice(-2)}`;
      } else {
        return '';
      }
    } else if (parts.length === 1) {
      // Chiffres tapes a la suite, sans separateur. Une annee gravable est toujours
      // 1900-2099 : c'est ce qui permet de distinguer les formats sans deviner.
      const n = parts[0];
      const annee = (x) => x.length === 4 && +x >= 1900 && +x <= 2099;
      const mois = (x) => +x >= 1 && +x <= 12;
      if (n.length === 4) {
        if (annee(n)) candidat = n;                                        // 2024
        else if (mois(n.slice(0, 2))) candidat = `${n.slice(0, 2)}/${n.slice(2)}`;  // 0324 -> 03/24
        else return '';
      } else if (n.length === 6) {
        if (mois(n.slice(0, 2)) && annee(n.slice(2))) candidat = `${n.slice(0, 2)}/${n.slice(4)}`;  // 032024 -> 03/24
        else candidat = `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;                       // 140324
      } else if (n.length === 8) {
        const enDate = `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(6)}`;
        const dateOk = this.#isValidEngravingDate(`${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`);
        const a = n.slice(0, 4), b = n.slice(4);
        const periodeOk = annee(a) && annee(b);
        // 20122024 = 20/12/2024 OU 2012-2024 : sur un bijou grave, on ne devine pas.
        if (dateOk && periodeOk) return '';
        if (periodeOk) candidat = +a <= +b ? `${a}-${b}` : `${b}-${a}`;    // 20122024 -> ambigu ; 20242012 -> 2012-2024
        else candidat = enDate;                                             // 14032024 -> 14/03/24
      } else {
        return '';
      }
    } else {
      return '';
    }

    return this.#isValidEngravingDate(candidat) ? candidat : '';
  }

  #setActiveChip(chips, active) {
    chips.forEach((c) => c.classList.toggle('bundle-chip--active', c === active));
  }

  #pawsCount() {
    const n = parseInt(this.state.paws, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  #currentVariant() {
    return this.data.variants.find(
      (v) =>
        v.options[this.finitionIndex] === this.state.finition &&
        v.options[this.pawsIndex] === this.state.paws
    );
  }


  // ---- Aperçu en direct de la gravure --------------------------------------
  // Règles reprises de auto-batch.mjs : majuscules, espaces non comptés,
  // 10 pt jusqu'à 5 lettres, 9 pt de 6 à 8, 7 pt au-delà.
  static SIZE_BY_LETTERS = { 2: 10, 3: 10, 4: 10, 5: 10, 6: 9, 7: 9, 8: 9, 9: 7, 10: 7 };
  static PAW_W = 16.6;
  static PAW_H = 12.29;
  static STEP  = 14.2;   // écart entre 2 pattes : les anneaux se chevauchent
  // Profil du coussinet relevé sur le plan fournisseur [hauteur mm, largeur dispo mm]
  static PROFIL = [[8.40,8.60],[8.58,8.35],[8.85,8.53],[9.13,8.72],[9.41,8.85],
                   [9.68,8.99],[9.96,9.13],[10.24,9.13],[10.51,9.13],[10.79,9.13],
                   [11.07,9.04],[11.25,8.60]];
  static ZONE_HAUT = 7.85;
  static ZONE_BAS  = 10.65;
  static MARGE     = 0.30;
  static PX_PAR_MM = 5.8;   // echelle d'affichage : 1 patte ≈ 100 px de large
  static GLYPHES = /[A-Z0-9 '\-\u00C0\u00C2\u00C4\u00C7\u00C9\u00C8\u00CA\u00CB\u00CE\u00CF\u00D4\u00D6\u00D9\u00DB\u00DC\u0178\u00D1]/;

  #nettoyer(txt) {
    const src = (txt || '').toUpperCase();
    let out = '';
    for (const ch of src) {
      if (ch === ' ') { out += ' '; continue; }
      if (BundleOffers.GLYPHES.test(ch)) { out += ch; continue; }
      const d = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (d && BundleOffers.GLYPHES.test(d)) out += d;
    }
    return out.replace(/\s+/g, ' ').trim();
  }

  #pointsPour(n) {
    if (n === 0 || n <= 2) return 10;
    return BundleOffers.SIZE_BY_LETTERS[n] || 7;
  }

  #dispoA(y) {
    const P = BundleOffers.PROFIL;
    if (y <= P[0][0]) return P[0][1];
    if (y >= P[P.length - 1][0]) return P[P.length - 1][1];
    for (let i = 1; i < P.length; i++) {
      if (y <= P[i][0]) {
        const a = P[i - 1], b = P[i], t = (y - a[0]) / (b[0] - a[0]);
        return a[1] + t * (b[1] - a[1]);
      }
    }
    return P[P.length - 1][1];
  }

  #dispoBande(y0, y1) {
    let m = Infinity;
    for (let y = y0; y <= y1; y += 0.1) m = Math.min(m, this.#dispoA(y));
    return m - BundleOffers.MARGE;
  }

  #construireApercu() {
    if (!this.apercuG || !this.apercuSvg) return;
    const n = this.#pawsCount();
    const largeur = BundleOffers.PAW_W + (n - 1) * BundleOffers.STEP;
    this.apercuSvg.setAttribute('viewBox', `-0.3 -0.3 ${(largeur + 0.6).toFixed(2)} ${(BundleOffers.PAW_H + 0.6).toFixed(2)}`);
    // Echelle fixe : une patte fait toujours la meme taille, qu'il y en ait 1 ou 5.
    this.apercuSvg.style.width = `${((largeur + 0.6) * BundleOffers.PX_PAR_MM).toFixed(0)}px`;

    const NS = 'http://www.w3.org/2000/svg';
    const frag = document.createDocumentFragment();
    for (let i = 0; i < n; i++) {
      const g = document.createElementNS(NS, 'g');
      g.setAttribute('class', 'lmdb-patte');
      g.setAttribute('data-patte', String(i));
      g.setAttribute('transform', `translate(${(i * BundleOffers.STEP).toFixed(2)} 0)`);

      const rect = document.createElementNS(NS, 'rect');
      rect.setAttribute('x', '-0.3'); rect.setAttribute('y', '-0.3');
      rect.setAttribute('width', (BundleOffers.PAW_W + 0.6).toFixed(2));
      rect.setAttribute('height', (BundleOffers.PAW_H + 0.6).toFixed(2));
      rect.setAttribute('clip-path', 'url(#lmdbPatte)');
      rect.setAttribute('data-ref', 'metal');
      g.appendChild(rect);

      const t = document.createElementNS(NS, 'text');
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('x', (BundleOffers.PAW_W / 2).toFixed(2));
      t.setAttribute('font-family', 'Amatic SC, cursive');
      t.setAttribute('font-weight', '400');
      t.setAttribute('data-ref', 'grave');
      g.appendChild(t);

      frag.appendChild(g);
    }
    this.apercuG.replaceChildren(frag);
    this.#majApercu();
  }

  #majApercu() {
    if (!this.apercuG) return;
    const argent = String(this.state.finition || '').toLowerCase().includes('argent');
    const remplissage = argent ? 'url(#lmdbArgent)' : 'url(#lmdbOr)';
    const encre = argent ? '#3F4449' : '#5A4520';
    const inputs = [...this.namesGrid.querySelectorAll('input')];

    this.apercuG.querySelectorAll('.lmdb-patte').forEach((g, i) => {
      g.querySelector('[data-ref="metal"]').setAttribute('fill', remplissage);
      const t = g.querySelector('[data-ref="grave"]');
      const saisi = this.#nettoyer(inputs[i] ? inputs[i].value : '');
      // Champ vide : on grave « PRÉNOM » en transparence, pour qu'on comprenne
      // tout de suite que c'est l'aperçu et pas une photo du produit.
      const nom = saisi || 'PRÉNOM';
      t.textContent = nom;
      t.setAttribute('fill', encre);
      t.setAttribute('fill-opacity', saisi ? '1' : '0.32');

      const lettres = nom.replace(/ /g, '').length;
      let mm = this.#pointsPour(lettres) * 25.4 / 72;
      const poser = (taille) => {
        const capH = 0.62 * taille;
        const base = (BundleOffers.ZONE_HAUT + BundleOffers.ZONE_BAS) / 2 + capH / 2;
        t.setAttribute('font-size', taille.toFixed(3));
        t.setAttribute('y', base.toFixed(2));
        return { capH, base };
      };
      let g2 = poser(mm);
      let larg = 0;
      try { larg = t.getComputedTextLength(); } catch (e) {}
      const dispo = this.#dispoBande(g2.base - g2.capH, g2.base);
      if (larg > dispo && larg > 0) poser(mm * (dispo / larg));   // on resserre juste ce qu'il faut
    });

    if (this.apercuLegende) {
      const rempli = inputs.some((i) => this.#nettoyer(i.value).length > 0);
      this.apercuLegende.textContent = rempli
        ? 'Aperçu de votre gravure'
        : 'Aperçu : écrivez le prénom ci-dessous';
    }
  }

  #renderNameFields() {
    const count = this.#pawsCount();
    this.namesGrid.querySelectorAll('input').forEach((input, i) => {
      this.names[i] = input.value;
    });

    const fields = [];
    for (let i = 0; i < count; i++) {
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'bundle-name-input';
      input.maxLength = 10;
      // « animal » ne figure dans aucun des 1020 verbatims clients : elles disent
      // « bébé », « mon compagnon », « mon loulou ». On s'aligne sur leur langue.
      input.placeholder = count > 1 ? `Prénom n°${i + 1}` : 'Son prénom';
      input.dataset.index = String(i);
      if (this.names[i]) input.value = this.names[i];
      input.addEventListener('input', () => {
        // Des clientes tapent la date DANS le prenom (#3251 : « Galou2011 », et
        // « Bacchus 2014 » tronque a 10 caracteres en « Bacchus 20 »), alors que la
        // date se saisit dans l'option dediee juste en dessous. Un prenom d'animal
        // ne contient jamais de chiffre : on les retire et on dit ou aller.
        if (/[0-9]/.test(input.value)) {
          input.value = input.value.replace(/[0-9]/g, '').replace(/\s{2,}/g, ' ').trimStart();
          this.#direOuMettreLaDate();
        } else if (this.errorEl) {
          this.errorEl.hidden = true;
        }
        input.classList.remove('bundle-name-input--error');
        this.#majApercu();
      });
      const idx = i;
      const surligner = (on) => {
        if (!this.apercuG) return;
        // Le champ actif met « sa » patte en avant : les autres reculent d'un cran,
        // l'association champ -> patte se fait sans explication.
        if (this.apercuEl) this.apercuEl.classList.toggle('lmdb-apercu--focus', on);
        this.apercuG.querySelectorAll('.lmdb-patte').forEach((g, k) =>
          g.classList.toggle('lmdb-patte--active', on && k === idx));
      };
      input.addEventListener('focus', () => surligner(true));
      input.addEventListener('blur',  () => surligner(false));
      fields.push(input);
    }
    this.namesGrid.replaceChildren(...fields);
  }

  /** Message d'aiguillage quand une cliente tape des chiffres dans le champ prenom. */
  #direOuMettreLaDate() {
    if (!this.errorEl) return;
    if (this.#defaultErrorText === undefined) this.#defaultErrorText = this.errorEl.textContent;
    this.errorEl.textContent =
      "Le prenom se grave en lettres. Pour graver une date, coche « Ajouter une date au dos de la patte » juste en dessous.";
    this.errorEl.hidden = false;
    clearTimeout(this.dateHintTimer);
    this.dateHintTimer = setTimeout(() => {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = this.#defaultErrorText;
    }, 6000);
  }

  #collectProperties() {
    const props = {};
    const inputs = this.namesGrid.querySelectorAll('input');
    const count = inputs.length;
    inputs.forEach((input, i) => {
      // Libellé visible au panier, sur la commande et sur la facture.
      // auto-batch.mjs filtre les propriétés sur /pr[ée]nom|gravure/i : garder le mot
      // « prénom » dans la clé est OBLIGATOIRE, sinon la gravure ne voit plus le nom.
      const key = count > 1 ? `Prénom n°${i + 1}` : 'Prénom gravé';
      props[key] = input.value.trim();
    });
    return props;
  }

  // Formats gravables acceptés : JJ/MM/AA, AAAA-AAAA, MM/AA, AAAA.
  #isValidEngravingDate(raw) {
    const value = (raw || '').trim();
    let m;

    // JJ/MM/AA (format gravé) et JJ/MM/AAAA (toléré en saisie) — date réelle,
    // donc 31/02 ou 00/00 sont refusés.
    if ((m = value.match(/^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/))) {
      const d = +m[1], mo = +m[2];
      const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
      if (y < 1900 || y > 2099 || mo < 1 || mo > 12 || d < 1) return false;
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    }
    // AAAA-AAAA — période de vie, la 1re année ne peut pas être après la 2e
    if ((m = value.match(/^(\d{4})-(\d{4})$/))) {
      const a = +m[1], b = +m[2];
      return a >= 1900 && b <= 2099 && a <= b;
    }
    // MM/AA
    if ((m = value.match(/^(\d{2})\/(\d{2})$/))) {
      const mo = +m[1];
      return mo >= 1 && mo <= 12;
    }
    // AAAA
    if ((m = value.match(/^(\d{4})$/))) {
      const y = +m[1];
      return y >= 1900 && y <= 2099;
    }
    return false;
  }

  #validate() {
    const inputs = [...this.namesGrid.querySelectorAll('input')];
    let valid = true;
    inputs.forEach((input) => {
      if (input.value.trim() === '') {
        input.classList.add('bundle-name-input--error');
        valid = false;
      }
    });
    let dateInvalide = false;
    let aucuneDate = false;
    if (this.#pouchSelected() && this.pouchGrid) {
      // Les dates sont FACULTATIVES patte par patte : beaucoup de clientes n'ont
      // qu'un seul animal parti. On exige seulement que celles qui sont remplies
      // soient gravables, et qu'il y en ait au moins une (sinon l'option est payee
      // pour rien).
      let remplies = 0;
      this.pouchGrid.querySelectorAll('input').forEach((input) => {
        const v = input.value.trim();
        if (v === '') return;
        remplies += 1;
        const propre = this.#normaliserDate(v);
        if (propre) {
          input.value = propre;
        } else {
          input.classList.add('bundle-name-input--error');
          valid = false;
          dateInvalide = true;
        }
      });
      if (remplies === 0) {
        aucuneDate = true;
        valid = false;
      }
    }
    if (this.errorEl) {
      if (this.#defaultErrorText === undefined) {
        this.#defaultErrorText = this.errorEl.textContent;
      }
      if (dateInvalide) {
        this.errorEl.textContent = 'Date à graver : formats acceptés — 14/03/24, 2012-2024, 03/24 ou 2024.';
      } else if (aucuneDate) {
        this.errorEl.textContent = "Indiquez au moins une date à graver, ou désactivez l'option.";
      } else {
        this.errorEl.textContent = this.#defaultErrorText;
      }
    }
    // On masque aussi le message quand tout est bon : sinon une erreur précédente
    // restait affichée après un ajout réussi.
    if (this.errorEl) this.errorEl.hidden = valid;
    return valid;
  }

  #updatePrices() {
    const variant = this.#currentVariant();
    if (!variant) {
      this.atc.disabled = true;
      return;
    }
    this.atc.disabled = false;

    const unit = variant.price;
    const unitCompare = variant.compareAt && variant.compareAt > unit ? variant.compareAt : unit;
    const extra = this.#pouchSelected() ? this.pouchPrice : 0;

    this.#setText('offer1-price', formatMoney(unit + extra));
    this.#setCompare('offer1-compare', unitCompare + extra, unit + extra);
    this.#setText('atc-price', formatMoney(unit + extra));
    this.#setCompare('atc-compare', unitCompare + extra, unit + extra);
  }

  #setText(ref, value) {
    const el = this.querySelector(`[data-ref="${ref}"]`);
    if (el) el.textContent = value;
  }

  #setCompare(ref, compareCents, priceCents) {
    const el = this.querySelector(`[data-ref="${ref}"]`);
    if (!el) return;
    el.textContent = compareCents > priceCents ? formatMoney(compareCents) : '';
  }

  async #updateGalleryImage() {
    const variant = this.#currentVariant();
    if (!variant || !this.productUrl) return;
    this.galleryAbort?.abort();
    this.galleryAbort = new AbortController();
    try {
      const url = `${this.productUrl}?variant=${variant.id}`;
      const response = await fetch(url, { signal: this.galleryAbort.signal });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.dispatchEvent(
        new VariantUpdatedEvent({ id: variant.id }, 'bundle-offers', { html, productId: this.productId })
      );
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  async #addToCart() {
    const variant = this.#currentVariant();
    if (!variant) return;
    if (!this.#validate()) return;

    const properties = this.#collectProperties();
    const items = [];

    if (this.#pouchSelected() && this.pouchGrid) {
      const dateInputs = [...this.pouchGrid.querySelectorAll('input')];
      const dateProps = {};
      // Une commande peut contenir un collier ET un bracelet (souvent au meme prenom).
      // La ligne « Gravure date » etant un produit a part, rien n'indiquait a quel bijou
      // la date se rapportait : le PDF de gravure devait le deviner par la position dans
      // le panier. On etiquette donc la ligne avec le bijou et les prenoms concernes.
      // Proprietes prefixees « _ » : invisibles pour le client, presentes dans l'API.
      dateProps._bijou = /collier/i.test(this.dataset.productTitle || '') ? 'Collier' : 'Bracelet';
      const prenomsSaisis = Object.values(properties).map((v) => String(v).trim()).filter(Boolean);
      if (prenomsSaisis.length) dateProps._prenoms = prenomsSaisis.join(' | ');
      dateInputs.forEach((input, i) => {
        const key = dateInputs.length > 1
          ? `${this.pouchProperty} n°${i + 1}`
          : this.pouchProperty;
        // Patte sans date : on envoie QUAND MEME la propriete, avec un marqueur sans
        // chiffre. auto-batch.mjs apparie prenoms et dates par position et exige
        // autant de dates que de prenoms ; il jette ensuite les paires sans chiffre.
        // Un trou dans la liste lui ferait perdre la correspondance patte <-> date.
        dateProps[key] = input.value.trim() || '(sans date)';
      });
      items.push({ id: this.pouchVariantId, quantity: 1, properties: dateProps });
    }

    items.push({ id: variant.id, quantity: 1, properties });

    const giftProps = { _is_gift: 'true' };
    if (this.giftBadge) giftProps._gift_badge = this.giftBadge;

    // Guide : max 1 par commande
    if (this.guideVariantId) {
      const guideId = parseInt(this.guideVariantId, 10);
      const cart = await fetch('/cart.js').then((r) => r.json());
      const guideInCart = cart.items.some((i) => i.variant_id === guideId);
      if (!guideInCart) items.push({ id: this.guideVariantId, quantity: 1, properties: { ...giftProps } });
    }

    // Pochette velours : +1 par bracelet — Shopify fusionne automatiquement (même variant + mêmes props)
    if (this.boxVariantId) items.push({ id: this.boxVariantId, quantity: 1, properties: { ...giftProps } });

    this.atc.classList.add('is-loading');
    const sections = ['auto-gifts'];
    const drawer = document.querySelector('cart-drawer');
    if (drawer) sections.push(drawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const json = await response.json();
      if (json.status) throw new Error(json.description || json.message || 'Erreur panier');
      const cartIcon = document.querySelector('cart-icon');
      const currentItemCount = cartIcon?.getCurrentItemCount();
      const addedCount = items.reduce((acc, it) => acc + it.quantity, 0);
      const newItemCount = currentItemCount != null ? currentItemCount + addedCount : undefined;
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: newItemCount, source: 'product-form', sections: json.sections })
      );
    } catch (error) {
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message || error } }));
    } finally {
      this.atc.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('bundle-offers')) {
  customElements.define('bundle-offers', BundleOffers);
}
